/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Service, { service } from '@ember/service';
import { computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import moment from 'moment';

export default class TermsService extends Service {
  // terms is a hot mess
  // assume that everything is broken and nothing is as it seems
  @service
  store;

  @tracked
  hasData = false;
  @tracked
  terms = [];
  @tracked
  termsPromise = null;

  // find the most recent term that has finished in the case that we are currently
  // between two terms (ie: in the holidays)
  // this function could probably be more appropriately named
  _betweenTermCalc(reportDate) {
    let now = moment(reportDate);
    return this.terms.find((term) => {
      // TLDR: promises suck
      // term.next is a proxy, which causes promises where promises are not wanted.
      // we can access the intended object by calling .content on the promise - if there
      // is any chance the promise is going to be resolved then this will be checking
      // for a property called content on the intended object, rather than the content
      // of the promise itself
      return (
        now.isAfter(moment(term.get('end')), 'day') &&
        (isEmpty(term.get('next.content')) ||
          now.isBefore(moment(term.get('next.start')), 'day'))
      );
    });
  }

  currentTermAtDate(reportDate) {
    let currentTerm = this.terms.find((term) => {
      return term.isCurrentAtDate(reportDate);
    });
    if (isEmpty(currentTerm)) {
      // We're in between terms
      currentTerm = this._betweenTermCalc(reportDate);
    }
    return currentTerm;
  }

  getAcademicYearTermsForDate(previousRollover, nextRollover) {
    return this.terms.filter((term) => {
      return moment(term.start).isBetween(
        moment(previousRollover),
        moment(nextRollover),
        'day',
        '[]',
      );
    });
  }

  @computed('terms.@each.isCurrent')
  get currentTerm() {
    let currentTerm = this.terms.find((term) => term.isCurrent);
    if (isEmpty(currentTerm)) {
      // We're in between terms
      currentTerm = this._betweenTermCalc();
    }
    return currentTerm;
  }

  @computed('currentTerm')
  get currentOrNextTerm() {
    let nextTerm = this.get('currentTerm.next');
    if (!this.get('currentTerm.isCurrent') && !isEmpty(nextTerm)) {
      // "current" term is most likely the previous term, and we're between
      // terms.  Return the next term, as we have one available.
      return nextTerm;
    }
    return this.currentTerm;
  }

  @computed('currentTerm')
  get currentAcademicYearTerms() {
    return this.terms.filter(
      (term) => term.academicYear === this.currentTerm.academicYear,
    );
  }

  @computed('currentAcademicYearTerms.[]')
  get lastTermOfYear() {
    return this.terms.reduce((latestTerm, term) => {
      const termEnd = moment(term.get('end'));

      if (
        !latestTerm ||
        termEnd.isAfter(moment(latestTerm.get('end')), 'day')
      ) {
        return term;
      }

      return latestTerm;
    }, null);
  }

  @computed('lastTermOfYear')
  get endOfCalendarYear() {
    return this.lastTermOfYear.get('end');
  }

  init() {
    super.init(...arguments);

    let terms = this.store.peekAll('term');
    if (terms.length) {
      this.terms = terms;
      this.hasData = true;
    } else {
      this.loadTerms();
    }
  }

  loadTerms() {
    let promise = this.store.findAll('term').then((terms) => {
      this.terms = terms;
      this.hasData = true;
    });
    this.termsPromise = promise;
    return promise;
  }

  refresh() {
    if (this.hasData) {
      this.terms.update();
    }
  }

  waitUntilLoaded() {
    return new Promise((resolveThisPromise) => {
      if (this.hasData) {
        // Terms are already loaded.
        return resolveThisPromise();
      }
      let interval;
      let timeout = window.setTimeout(() => {
        // Loading terms due to timeout.
        let loadTerms = this.loadTerms();
        if (!isEmpty(interval)) {
          window.clearInterval(interval);
        }
        resolveThisPromise(loadTerms);
      }, 3000);
      interval = window.setInterval(() => {
        // Checking if loaded.
        if (this.hasData) {
          // Terms are loaded.
          window.clearTimeout(timeout);
          window.clearInterval(interval);
          resolveThisPromise();
        }
      }, 100);
    });
  }
}
